/**
 * useVersionCheck — polls for newer app versions and exposes update state.
 *
 * Strategy:
 *   - Web: fetches /version.json (static file, CDN-friendly, 5-min poll)
 *   - Native (iOS/Android): fetches /api/version from the production server
 *   - Also checks on foreground (via AppLifecycleHub) and on online event
 *
 * When a newer version is detected, exposes { updateAvailable, latestVersion,
 * handleUpdate, handleDismiss } for the UI to render a prompt.
 *
 * Dismissal logic:
 *   - User can dismiss the prompt → stored in localStorage
 *   - Re-shows after 1 hour (DISMISS_COOLDOWN_MS)
 *   - Safety: max 1 auto-reload per 5 minutes (prevents loops)
 *
 * Platform actions:
 *   - Web: window.location.reload()
 *   - iOS: Browser.open() → App Store link
 *   - Android: Browser.open() → Play Store link
 *
 * @see VERSION_UPDATE_PLAN.md — Fas 2 & 3
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { Capacitor } from "@capacitor/core"
import { APP_VERSION } from "@/config/constants"
import { publicRequest } from "@/shared/utils/api/apiClient"
import { isNewerVersion } from "@/shared/utils/version/versionUtils"
import { appLifecycleHub } from "@/shared/utils/platform/appLifecycleHub"

// ── Configuration ─────────────────────────────────────────────
const POLL_INTERVAL_MS = 15 * 60 * 1000 // 15 minutes
const DISMISS_COOLDOWN_MS = 60 * 60 * 1000 // 1 hour
const MIN_RELOAD_INTERVAL_MS = 5 * 60 * 1000 // 5 min safety guard

const LS_DISMISSED_VERSION = "version_dismissed"
const LS_DISMISSED_TIME = "version_dismissed_time"
const LS_LAST_RELOAD_TIME = "version_last_reload_time"

// Production base URL for native clients (they can't fetch relative paths)
const PRODUCTION_BASE_URL = "https://totodo.app"

interface VersionResponse {
  version: string
  buildNumber?: string
  timestamp?: string
  updateUrl?: {
    ios?: string
    android?: string
    web?: string
  }
}

export interface VersionCheckState {
  /** True when a newer version is detected and not dismissed */
  updateAvailable: boolean
  /** The latest version string from the server */
  latestVersion: string | null
  /** Trigger the platform-appropriate update action */
  handleUpdate: () => void
  /** Dismiss the prompt (re-shows after cooldown) */
  handleDismiss: () => void
}

// ── Default update URLs ───────────────────────────────────────
const DEFAULT_UPDATE_URLS = {
  ios: "https://apps.apple.com/app/to-to-do/id1669928204",
  android: "https://play.google.com/store/apps/details?id=app.totodo.com",
  web: "https://totodo.app",
}

/**
 * Fetch the latest version info from the appropriate source.
 */
async function fetchLatestVersion(): Promise<VersionResponse | null> {
  try {
    const platform = Capacitor.getPlatform()
    // Web: fetch relative /version.json (works with CDN / same origin)
    // Native: fetch from production server API
    const url =
      platform === "web"
        ? "/version.json"
        : `${PRODUCTION_BASE_URL}/api/version`

    const res = await publicRequest<VersionResponse>({
      url,
      method: "GET",
      headers: { Accept: "application/json" },
    })
    if (!res.ok) return null
    return res.data
  } catch {
    // Network error, offline, etc. — silent fail
    return null
  }
}

/**
 * Check if the dismiss cooldown has elapsed.
 */
function isDismissCooldownActive(latestVersion: string): boolean {
  try {
    const dismissedVersion = localStorage.getItem(LS_DISMISSED_VERSION)
    const dismissedTime = localStorage.getItem(LS_DISMISSED_TIME)
    if (!dismissedVersion || !dismissedTime) return false
    if (dismissedVersion !== latestVersion) return false // new version → show again
    return Date.now() - Number(dismissedTime) < DISMISS_COOLDOWN_MS
  } catch {
    return false
  }
}

/**
 * Safety guard: prevent reload loops.
 */
function canReload(): boolean {
  try {
    const lastReload = localStorage.getItem(LS_LAST_RELOAD_TIME)
    if (!lastReload) return true
    return Date.now() - Number(lastReload) > MIN_RELOAD_INTERVAL_MS
  } catch {
    return true
  }
}

export function useVersionCheck(): VersionCheckState {
  const [latestVersion, setLatestVersion] = useState<string | null>(null)
  const [updateUrl, setUpdateUrl] = useState(DEFAULT_UPDATE_URLS)
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // ── Core check logic ──────────────────────────────────────
  const checkVersion = useCallback(async () => {
    const data = await fetchLatestVersion()
    if (!data?.version) return

    setLatestVersion(data.version)

    if (data.updateUrl) {
      setUpdateUrl((prev) => ({ ...prev, ...data.updateUrl }))
    }

    if (isNewerVersion(APP_VERSION, data.version)) {
      // Newer version exists — show prompt unless recently dismissed
      if (!isDismissCooldownActive(data.version)) {
        setUpdateAvailable(true)
      }
    } else {
      setUpdateAvailable(false)
    }
  }, [])

  // ── Update action (platform-aware) ────────────────────────
  const handleUpdate = useCallback(async () => {
    const platform = Capacitor.getPlatform()

    if (platform === "web") {
      if (!canReload()) {
        console.warn("[VersionCheck] Reload throttled — too recent")
        return
      }
      try {
        localStorage.setItem(LS_LAST_RELOAD_TIME, String(Date.now()))
      } catch {
        /* localStorage may be full */
      }
      window.location.reload()
    } else {
      // Native: open store link
      const storeUrl =
        platform === "ios" ? updateUrl.ios : updateUrl.android
      if (storeUrl) {
        try {
          const { Browser } = await import("@capacitor/browser")
          await Browser.open({ url: storeUrl })
        } catch {
          // Fallback: try window.open
          window.open(storeUrl, "_blank")
        }
      }
    }
  }, [updateUrl])

  // ── Dismiss action ────────────────────────────────────────
  const handleDismiss = useCallback(() => {
    setUpdateAvailable(false)
    if (latestVersion) {
      try {
        localStorage.setItem(LS_DISMISSED_VERSION, latestVersion)
        localStorage.setItem(LS_DISMISSED_TIME, String(Date.now()))
      } catch {
        /* localStorage may be full */
      }
    }
  }, [latestVersion])

  // ── Lifecycle: poll + foreground + online ──────────────────
  useEffect(() => {
    // Initial check (delayed 10s to not block startup)
    const initialTimeout = setTimeout(() => {
      void checkVersion()
    }, 10_000)

    // Periodic polling
    pollTimerRef.current = setInterval(() => {
      void checkVersion()
    }, POLL_INTERVAL_MS)

    // Foreground check (tab visible / app returns from background)
    const unsubForeground = appLifecycleHub.on("foreground", () => {
      void checkVersion()
    })

    // Online check (network restored)
    const handleOnline = () => {
      void checkVersion()
    }
    window.addEventListener("online", handleOnline)

    return () => {
      clearTimeout(initialTimeout)
      if (pollTimerRef.current) clearInterval(pollTimerRef.current)
      unsubForeground()
      window.removeEventListener("online", handleOnline)
    }
  }, [checkVersion])

  return {
    updateAvailable,
    latestVersion,
    handleUpdate,
    handleDismiss,
  }
}
